import { MailOutlined } from '@ant-design/icons'
import type { Carer } from '@lmc/ts-common'
import { Avatar, Heading, Icon, IconType, Popover, Text } from '@lmc/ui-kit'
import type React from 'react'
import styled from 'styled-components'
import { commonPatterns } from '../../../../utils/enhancedTestIds'
import { getCarerRole } from '../../utils/teamList'

type TeamListItemProps = {
  testId?: string
  carer: Carer
  onClick: React.MouseEventHandler<HTMLDivElement>
  firstItem?: boolean
  index?: number
}

const StyledContainer = styled.div<{ $firstItem?: boolean }>`
  background-color: ${(props) => props.theme.palette.white} !important;
  border-radius: 8px;
  margin-bottom: 10px;
  margin-top: ${(props) => (props?.$firstItem ? '24px' : '0px')};
  height: 64px;
  display: flex;
  align-items: center;
  padding: 16px 12px;
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  cursor: pointer;
  &:hover {
    background-color: ${(props) =>
      props.theme.color.neutral.color10} !important;
  }
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 0px;
  }
`

const StyledWrapper = styled.div`
  display: flex;
  margin-left: auto;
  margin-right: 0px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const StyledHeading = styled(Heading)`
  margin-left: 10px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-right: auto;
  }
`

const StyledText = styled(Text)`
  margin-left: 10px;
  font-size: 12px !important;
  min-width: 130px;
  text-align: center;
`

const StyledPhone = styled(Icon)`
  margin-left: 10px;
  margin-top: 8px;
  color: ${(props) => props.theme.color.neutral.color5} !important;
`
const IconWrapper = styled.div`
  &:hover {
    color: ${(props) => props.theme.color.hover.primary};
  }
  margin-left: 10px;
  margin-top: 6px;
  color: ${(props) => props.theme.color.neutral.color5} !important;
`
const StyledPaddingWrapper = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`

const TeamListItem: React.FC<TeamListItemProps> = ({
  testId,
  carer,
  onClick,
  firstItem,
  index
}) => {
  const picture = carer?.picture as unknown as string
  const name = `${carer?.name?.first} ${carer?.name?.last}`

  // Generate test IDs using enhanced system with index instead of carer.id
  const rowId = index !== undefined ? index : 0
  const itemTestId = testId ? commonPatterns.row(testId, rowId) : undefined
  const avatarTestId = testId ? `${testId}-row-avatar-${rowId}` : undefined
  const nameTestId = testId ? `${testId}-row-name-${rowId}` : undefined
  const emailTestId = testId ? `${testId}-row-email-${rowId}` : undefined
  const phoneTestId = testId ? `${testId}-row-phone-${rowId}` : undefined
  const roleTestId = testId ? `${testId}-row-role-${rowId}` : undefined

  return (
    <StyledPaddingWrapper>
      <StyledContainer
        onClick={onClick}
        $firstItem={firstItem}
        data-testid={itemTestId}
      >
        <Avatar url={picture} size={40} data-testid={avatarTestId} />
        <StyledHeading level={5} data-testid={nameTestId}>
          {name}
        </StyledHeading>
        {carer?.email && (
          <Popover
            content={
              <Text>
                <a href={`mailto:${carer.email}`}>{carer.email}</a>
              </Text>
            }
          >
            <IconWrapper data-testid={emailTestId}>
              <MailOutlined style={{ fontSize: '20px' }} />
            </IconWrapper>
          </Popover>
        )}
        {carer?.phoneNumber && (
          <Popover
            content={
              <Text>
                <a href={`tel:${carer.phoneNumber}`}>{carer.phoneNumber}</a>
              </Text>
            }
          >
            <div data-testid={phoneTestId}>
              <StyledPhone customIcon={IconType.Phone} />
            </div>
          </Popover>
        )}
        <StyledWrapper>
          <StyledText level={5} data-testid={roleTestId}>
            {getCarerRole(carer?.role)}
          </StyledText>
        </StyledWrapper>
      </StyledContainer>
    </StyledPaddingWrapper>
  )
}

export default TeamListItem
